import { useState } from "react";
import { toast } from "sonner";
import {
  Loader2, LogOut, Inbox, Package, Tag, Users, Wrench, Star, Newspaper,
  MapPin, Home as HomeIcon, Target, ListChecks, Gift, ShieldCheck, Layers, Image as ImageIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { formatApiError } from "@/lib/cms";
import { CrudManager } from "@/components/admin/CrudManager";
import { SettingsEditor } from "@/components/admin/SettingsEditor";
import { SubmissionsView } from "@/components/admin/SubmissionsView";
import { AdminsManager } from "@/components/admin/AdminsManager";

const ICON_OPTIONS = ["clock", "timer", "shield-check", "network", "truck", "heart-pulse", "badge-check", "handshake"];

export default function Admin() {
  const { user, login, logout } = useAuth();
  const { refresh, content } = useContent();
  const [tab, setTab] = useState("submissions");
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(creds.email, creds.password);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(formatApiError(err, "Invalid email or password"));
    } finally {
      setBusy(false);
    }
  };

  if (user === undefined) {
    return <div className="flex min-h-[70vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  if (!user) {
    const inp = "w-full rounded-lg border-2 border-input bg-background px-3 py-2.5 text-sm mt-2";
    return (
      <div className="flex min-h-[70vh] items-center justify-center px-4" data-testid="admin-login">
        <form onSubmit={doLogin} className="w-full max-w-sm border border-border bg-card p-8">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">CMS Login</h1>
          <p className="mt-2 text-sm text-muted-foreground">Sign in to manage your website content.</p>
          <label className="mt-6 block text-xs font-bold uppercase tracking-wide text-muted-foreground">Email</label>
          <input type="email" required value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} className={inp} data-testid="admin-email-input" />
          <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-muted-foreground">Password</label>
          <input type="password" required value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} className={inp} data-testid="admin-password-input" />
          <button type="submit" disabled={busy} data-testid="admin-login-btn" className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-bold uppercase tracking-wide text-white hover:bg-primary/90 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  const TAB_GROUPS = [
    {
      label: "General",
      tabs: [
        { id: "submissions", label: "Submissions", icon: Inbox },
        { id: "admins", label: "Admins", icon: ShieldCheck },
      ],
    },
    {
      label: "Home Page",
      tabs: [
        { id: "stats", label: "Homepage Stats", icon: ListChecks },
        { id: "group_strength", label: "Group Strength", icon: ShieldCheck },
        { id: "services", label: "Services", icon: Wrench },
        { id: "reviews", label: "Reviews", icon: Star },
        { id: "offer", label: "Offer Popup", icon: Gift },
      ],
    },
    {
      label: "Products Page",
      tabs: [
        { id: "products", label: "Products", icon: Package },
        { id: "product_categories", label: "Product Types", icon: Layers },
      ],
    },
    {
      label: "Partner Brands Page",
      tabs: [
        { id: "brands", label: "Partner Brands", icon: Tag },
      ],
    },
    {
      label: "About Page",
      tabs: [
        { id: "team", label: "Team", icon: Users },
        { id: "mission", label: "Mission & Vision", icon: Target },
        { id: "core_values", label: "Core Values", icon: Star },
      ],
    },
    {
      label: "News & Events Page",
      tabs: [
        { id: "news", label: "News & Events", icon: Newspaper },
      ],
    },
    {
      label: "Dealer Network Page",
      tabs: [
        { id: "dealers", label: "Dealer Map", icon: MapPin },
        { id: "dealer_stats", label: "Dealer Page Stats", icon: MapPin },
      ],
    },
    {
      label: "Support & Contact Page",
      tabs: [
        { id: "categories", label: "Support Categories", icon: ListChecks },
      ],
    },
    {
      label: "Site-Wide (All Pages)",
      tabs: [
        { id: "homepage", label: "Company Info", icon: HomeIcon },
        { id: "banners", label: "Page Banners", icon: ImageIcon },
        { id: "page_headers", label: "Page Headers", icon: HomeIcon },
      ],
    },
  ];

  const PRODUCT_CATEGORIES = (content.product_categories && content.product_categories.length)
    ? content.product_categories
    : ["Tyre", "Engine Oil"];

  const render = () => {
    switch (tab) {
      case "submissions": return <SubmissionsView />;
      case "products": return <CrudManager resource="products" title="Products" titleKey="name" subKey="brand" onChanged={refresh} fields={[
        { key: "name", label: "Name", type: "text" },
        { key: "category", label: "Category / Type", type: "select", options: PRODUCT_CATEGORIES },
        { key: "brand", label: "Brand", type: "text" },
        { key: "position", label: "Position", type: "text" },
        { key: "highlight", label: "Highlight", type: "text" },
        { key: "description", label: "Description", type: "textarea" },
        { key: "is_featured", label: "Show on Homepage (Featured)", type: "bool" },
        { key: "image", label: "Image", type: "image" },
        { key: "specs", label: "Specifications", type: "keyvalue" },
      ]} />;
      case "brands": return <CrudManager resource="brands" title="Partner Brands" titleKey="name" subKey="tag" onChanged={refresh} fields={[
        { key: "name", label: "Name", type: "text" },
        { key: "tag", label: "Tagline", type: "text" },
        { key: "logo", label: "Logo", type: "image" },
        { key: "desc", label: "Description", type: "textarea" },
        { key: "details", label: "Key Points (one per line)", type: "tags" },
      ]} />;
      case "team": return <CrudManager resource="team" title="Team Members" titleKey="name" subKey="role" onChanged={refresh} fields={[
        { key: "name", label: "Name", type: "text" },
        { key: "role", label: "Role", type: "text" },
        { key: "image", label: "Photo", type: "image" },
        { key: "bio", label: "Bio (shown on hover)", type: "textarea" },
      ]} />;
      case "services": return <CrudManager resource="services" title="Facilities & Services" titleKey="title" subKey="desc" onChanged={refresh} fields={[
        { key: "title", label: "Title", type: "text" },
        { key: "icon", label: "Icon", type: "select", options: ICON_OPTIONS },
        { key: "desc", label: "Description", type: "textarea" },
      ]} />;
      case "reviews": return <CrudManager resource="reviews" title="Customer Reviews" titleKey="name" subKey="location" onChanged={refresh} fields={[
        { key: "name", label: "Customer Name", type: "text" },
        { key: "location", label: "Location", type: "text" },
        { key: "rating", label: "Rating (1-5)", type: "number" },
        { key: "image", label: "Customer Photo (optional)", type: "image" },
        { key: "text", label: "Review", type: "textarea" },
      ]} />;
      case "news": return <CrudManager resource="news" title="News & Events" titleKey="title" subKey="date" onChanged={refresh} fields={[
        { key: "title", label: "Title", type: "text" },
        { key: "date", label: "Date (YYYY-MM-DD)", type: "text" },
        { key: "tag", label: "Category Tag", type: "text" },
        { key: "image", label: "Image", type: "image" },
        { key: "excerpt", label: "Excerpt", type: "textarea" },
        { key: "body", label: "Body (one paragraph per line)", type: "paragraphs" },
      ]} />;
      case "dealers": return <CrudManager resource="dealers" title="Dealer Map Pins" titleKey="name" subKey="region" onChanged={refresh} fields={[
        { key: "name", label: "Location Name (e.g. Dhaka)", type: "text" },
        { key: "region", label: "Division / Region", type: "text" },
        { key: "dealer_count", label: "Number of Dealers", type: "number" },
        { key: "retail_count", label: "Number of Retailers", type: "number" },
        { key: "lat", label: "Latitude", type: "number" },
        { key: "lng", label: "Longitude", type: "number" },
      ]} />;
      case "homepage": return <SettingsEditor settingKey="company" title="Company Info" onChanged={refresh} fields={[
        { key: "name", label: "Company Name", type: "text" },
        { key: "short", label: "Short Name", type: "text" },
        { key: "heroTitle", label: "Hero Title (main line)", type: "text" },
        { key: "heroTitleAccent", label: "Hero Title Accent Word (highlighted)", type: "text" },
        { key: "tagline", label: "Tagline", type: "text" },
        { key: "footerText", label: "Footer About Text", type: "textarea" },
        { key: "established", label: "Established Year", type: "text" },
        { key: "address", label: "Address", type: "textarea" },
        { key: "phone", label: "Phone", type: "text" },
        { key: "email", label: "Email", type: "text" },
        { key: "intro", label: "Intro Paragraph", type: "textarea" },
        { key: "storyParagraph2", label: "About Page — Story Paragraph 2", type: "textarea" },
        { key: "storyParagraph3", label: "About Page — Story Paragraph 3", type: "textarea" },
        { key: "heroImage", label: "Hero Image", type: "image" },
        { key: "aboutImage", label: "About Image", type: "image" },
        { key: "mapImage", label: "Dealer/Map Banner Image", type: "image" },
      ]} />;
      case "stats": return <SettingsEditor settingKey="stats" title="Homepage Stats" mode="objectlist" onChanged={refresh} itemFields={[
        { key: "value", label: "Value", type: "text" },
        { key: "label", label: "Label", type: "text" },
      ]} />;
      case "group_strength": return <SettingsEditor settingKey="group_strength" title="Group Strength Cards (Homepage — Built On Precision & Trust)" mode="objectlist" onChanged={refresh} itemFields={[
        { key: "icon", label: "Icon", type: "select", options: ["truck", "badge-check", "shield-check", "handshake", "clock", "timer", "network", "heart-pulse"] },
        { key: "title", label: "Title", type: "text" },
        { key: "desc", label: "Description", type: "textarea" },
      ]} />;
      case "dealer_stats": return <SettingsEditor settingKey="dealer_stats" title="Dealer Network Page Stats" mode="objectlist" onChanged={refresh} itemFields={[
        { key: "icon", label: "Icon", type: "select", options: ["store", "building", "network", "users", "map-pin", "truck", "badge-check"] },
        { key: "value", label: "Value", type: "text" },
        { key: "label", label: "Label", type: "text" },
      ]} />;
      case "banners": return <SettingsEditor settingKey="page_banners" title="Page Banner Images" onChanged={refresh} fields={[
        { key: "products", label: "Products Page Banner", type: "image" },
        { key: "brands", label: "Partner Brands Page Banner", type: "image" },
        { key: "about", label: "About Page Banner", type: "image" },
        { key: "news", label: "News & Events Page Banner", type: "image" },
        { key: "support", label: "Support & Contact Page Banner", type: "image" },
      ]} />;
      case "page_headers": return <SettingsEditor settingKey="page_headers" title="Page Headers (Eyebrow, Title, Description)" onChanged={refresh} fields={[
        { key: "products_eyebrow", label: "Products — Eyebrow", type: "text" },
        { key: "products_title", label: "Products — Title", type: "text" },
        { key: "products_description", label: "Products — Description", type: "textarea" },
        { key: "brands_eyebrow", label: "Partner Brands — Eyebrow", type: "text" },
        { key: "brands_title", label: "Partner Brands — Title", type: "text" },
        { key: "brands_description", label: "Partner Brands — Description", type: "textarea" },
        { key: "about_eyebrow", label: "About — Eyebrow", type: "text" },
        { key: "about_title", label: "About — Title", type: "text" },
        { key: "about_description", label: "About — Description", type: "textarea" },
        { key: "news_eyebrow", label: "News — Eyebrow", type: "text" },
        { key: "news_title", label: "News — Title", type: "text" },
        { key: "news_description", label: "News — Description", type: "textarea" },
        { key: "support_eyebrow", label: "Support — Eyebrow", type: "text" },
        { key: "support_title", label: "Support — Title", type: "text" },
        { key: "support_description", label: "Support — Description", type: "textarea" },
        { key: "about_story_title", label: "About / Story — Title", type: "text" },
        { key: "about_mission_eyebrow", label: "About / Mission & Vision — Eyebrow", type: "text" },
        { key: "about_mission_title", label: "About / Mission & Vision — Title", type: "text" },
        { key: "about_mission_description", label: "About / Mission & Vision — Description", type: "textarea" },
        { key: "about_values_eyebrow", label: "About / Core Values — Eyebrow", type: "text" },
        { key: "about_values_title", label: "About / Core Values — Title", type: "text" },
        { key: "about_values_description", label: "About / Core Values — Description", type: "textarea" },
        { key: "about_stats_eyebrow", label: "About / Group Strength — Eyebrow", type: "text" },
        { key: "about_stats_title", label: "About / Group Strength — Title", type: "text" },
        { key: "about_stats_description", label: "About / Group Strength — Description", type: "textarea" },
        { key: "about_team_eyebrow", label: "About / Leadership — Eyebrow", type: "text" },
        { key: "about_team_title", label: "About / Leadership — Title", type: "text" },
        { key: "about_team_description", label: "About / Leadership — Description", type: "textarea" },
      ]} />;
      case "offer": return <SettingsEditor settingKey="offer" title="Homepage Offer Popup" onChanged={refresh} fields={[
        { key: "enabled", label: "Show popup", type: "bool" },
        { key: "badge", label: "Badge", type: "text" },
        { key: "title", label: "Title", type: "text" },
        { key: "description", label: "Description", type: "textarea" },
        { key: "image", label: "Image", type: "image" },
        { key: "ctaLabel", label: "Button Label", type: "text" },
        { key: "ctaCategory", label: "Support Category on click", type: "text" },
      ]} />;
      case "mission": return <SettingsEditor settingKey="mission_vision" title="Mission, Vision & Core Values" onChanged={refresh} fields={[
        { key: "vision", label: "Vision (one paragraph per line)", type: "paragraphs" },
        { key: "mission", label: "Mission (one paragraph per line)", type: "paragraphs" },
      ]} />;
      case "core_values": return <SettingsEditor settingKey="core_values" title="Core Values of Runner Group" mode="objectlist" onChanged={refresh} itemFields={[
        { key: "letter", label: "Letter (fallback if no image)", type: "text" },
        { key: "text", label: "Statement", type: "text" },
        { key: "image", label: "Custom Icon Image (optional — replaces the letter)", type: "image" },
      ]} />;
      case "categories": return <SettingsEditor settingKey="support_categories" title="Support Form Categories" mode="tags" onChanged={refresh} />;
      case "product_categories": return <SettingsEditor settingKey="product_categories" title="Product Categories / Types" mode="tags" onChanged={refresh} />;
      case "admins": return <AdminsManager />;
      default: return null;
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="admin-dashboard">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">Content Manager</h1>
          <p className="mt-1 text-sm text-muted-foreground">Signed in as {user.email}</p>
        </div>
        <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 rounded-lg border-2 border-border px-4 py-2 text-xs font-bold uppercase tracking-wide text-foreground hover:border-primary hover:text-primary">
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </div>

      <div className="grid gap-8 lg:grid-cols-[240px_1fr]">
        <aside className="h-fit border border-border bg-card p-2 lg:sticky lg:top-24">
          {TAB_GROUPS.map((group, gi) => (
            <div key={group.label}>
              <div className={`px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70 ${gi === 0 ? "pt-1" : "pt-4"}`}>
                {group.label}
              </div>
              {group.tabs.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    data-testid={`tab-${t.id}`}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors ${tab === t.id ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                  >
                    <Icon className="h-4 w-4 shrink-0" /> {t.label}
                  </button>
                );
              })}
            </div>
          ))}
        </aside>
        <section className="min-w-0" key={tab}>{render()}</section>
      </div>
    </div>
  );
}
